import React, { type JSX } from 'react';
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config';

export interface PrefetchUrlConfig {
    hostname: string;
    addCrossOrigin: boolean;
}

/**
 * Will generate the links to preconnect to a hostname and to prefetch a hostname.
 *
 * @param urlsToPrefetch An array of objects containing the hostname and whether to add crossOrigin.
 * @returns The links to prefetch/preconnect the urls.
 */
export function getPrefetchUrls(urlsToPrefetch: PrefetchUrlConfig[]): JSX.Element[] | null {
    return [
        ...urlsToPrefetch,
        {
            hostname: new URL(getConfig().bffBaseUrl).hostname,
            addCrossOrigin: false,
        },
    ].flatMap((resource) => [
        <link
            key={resource.hostname}
            rel="preconnect"
            href={`//${resource.hostname}`}
            {...(resource.addCrossOrigin ? { crossOrigin: 'anonymous' } : {})}
        />,
        <link
            key={`${resource.hostname}-dns`}
            rel="dns-prefetch"
            href={`//${resource.hostname}`}
        />,
    ]);
}

export function getPreloadFontUrls(fontsToPreload: string[]): JSX.Element[] | null {
    return fontsToPreload.map((fontUrl) => (
        <link
            key={fontUrl}
            rel="preload"
            as="font"
            href={getAssetUrl(fontUrl)}
            crossOrigin="anonymous"
        />
    ));
}

/**
 * Will generate the links to load a stylesheet asynchronously. It creates two links for each style sheet, one with rel=preload
 * and one with rel=stylesheet with the media=print trick.
 *
 * @param stylesheetUrlsToPreload The urls of the stylesheets to preload.
 * @returns The links to load the stylesheets asynchronously.
 */
export function getPreloadStylesheets(stylesheetUrlsToPreload: string[]): JSX.Element[] | null {
    return stylesheetUrlsToPreload.flatMap((stylesheetUrl) => [
        <link key={`${stylesheetUrl}-preload`} rel="preload" as="style" href={stylesheetUrl} />,
        <link
            key={stylesheetUrl}
            rel="stylesheet"
            href={stylesheetUrl}
            media="print"
            // @ts-expect-error types do not allow text, which is also valid - however hacky
            onLoad="this.media='all'"
        />,
    ]);
}
